<script setup lang="ts">
import { computed, ref } from 'vue';
import {
  mdiCalendarRangeOutline,
  mdiDomain,
  mdiGoogleClassroom,
  mdiInformationOutline,
} from '@quasar/extras/mdi-v7';
import SchoolEnrollmentStatusDialog from 'src/modules/School/components/SchoolEnrollmentStatusDialog/SchoolEnrollmentStatusDialog.vue';
import SchoolEnrollmentStatusBadge from 'src/modules/School/components/SchoolEnrollmentStatusBadge/SchoolEnrollmentStatusBadge.vue';
import { enrollmentStatusMeta } from 'src/modules/School/utilities/enrollmentStatusMeta';
import type { StudentClass } from 'src/modules/School/composables/useStudentClasses';
import type { StudentSemesterGroup } from 'src/modules/School/composables/useStudentSemesterOverview';

const props = defineProps<{
  groups: StudentSemesterGroup[];
  loading?: boolean | undefined;
}>();

const statusDialogOpen = ref(false);
const activeClass = ref<StudentClass | null>(null);

/** No semester at all to show, versus a semester that simply has no classes yet. */
const hasGroups = computed(() => props.groups.length > 0);
const hasAnyClass = computed(() =>
  props.groups.some((group) => group.classes.length > 0),
);

// Quasar exposes its brand colors as CSS vars; palette colors (e.g. the unknown
// status' `grey-5`) have no var, so the accent bar falls back to the theme border.
const BRAND_COLORS = new Set([
  'primary',
  'secondary',
  'accent',
  'positive',
  'negative',
  'info',
  'warning',
  'dark',
]);

function metaFor(studentClass: StudentClass) {
  return enrollmentStatusMeta(studentClass.status);
}

function accentFor(studentClass: StudentClass) {
  const { color } = metaFor(studentClass);
  return BRAND_COLORS.has(color) ? `var(--q-${color})` : 'var(--border-subtle)';
}

function openStatus(studentClass: StudentClass) {
  activeClass.value = studentClass;
  statusDialogOpen.value = true;
}
</script>

<template>
  <section class="q-mb-md">
    <div class="row items-center q-gutter-sm q-mb-sm">
      <q-icon
        :name="mdiGoogleClassroom"
        size="sm"
        color="primary"
      />
      <div class="text-subtitle1 text-weight-medium student-classes-heading">
        {{ $t('STUDENT_MY_CLASSES') }}
      </div>
    </div>

    <q-skeleton
      v-if="loading && !hasAnyClass"
      type="rect"
      height="112px"
      class="rounded-borders"
    />

    <div
      v-else-if="!hasGroups"
      class="student-classes-empty text-body2"
    >
      {{ $t('STUDENT_NO_CLASSES') }}
    </div>

    <!--
      One block per semester the student can be in — the current one and what is
      coming — so an enrollment made for a future term is visible straight away
      instead of hiding behind a filter the student never set.
    -->
    <template v-else>
      <div
        v-for="group in groups"
        :key="group.semester.documentId"
        class="q-mb-md"
      >
        <div class="row items-center q-gutter-xs q-mb-sm">
          <q-icon
            :name="mdiCalendarRangeOutline"
            size="xs"
            class="student-class-school"
          />
          <div class="text-body2 text-weight-medium student-classes-heading">
            {{ group.semester.name }}
          </div>
          <q-chip
            dense
            square
            :color="group.isCurrent ? 'primary' : 'grey-6'"
            text-color="dark"
            class="student-semester-tag"
          >
            {{ group.isCurrent ? $t('STUDENT_SEMESTER_CURRENT') : $t('STUDENT_SEMESTER_UPCOMING') }}
          </q-chip>
        </div>

        <div
          v-if="!group.classes.length"
          class="student-classes-empty text-body2"
        >
          {{ $t('STUDENT_NO_CLASSES_IN_SEMESTER') }}
        </div>

        <div
          v-else
          class="row q-col-gutter-md"
        >
          <div
            v-for="studentClass in group.classes"
            :key="studentClass.enrollmentDocumentId"
            class="col-12 col-sm-6 col-md-4"
          >
            <q-card
              flat
              class="student-class-card full-height"
              :style="{ '--status-color': accentFor(studentClass) }"
            >
              <q-card-section class="q-pb-sm">
                <div class="row items-center no-wrap q-gutter-xs student-class-school">
                  <q-icon
                    :name="mdiDomain"
                    size="xs"
                  />
                  <div class="text-caption ellipsis">
                    {{ studentClass.schoolName || '—' }}
                  </div>
                </div>

                <div class="text-subtitle1 text-weight-medium q-mt-xs student-class-name">
                  {{ studentClass.className || '—' }}
                </div>
                <!-- The semester is the group heading now, so the card doesn't repeat it. -->
                <div
                  v-if="studentClass.classCode"
                  class="text-caption student-class-school"
                >
                  {{ studentClass.classCode }}
                </div>
              </q-card-section>

              <q-card-section class="q-pt-none row items-center justify-between q-gutter-sm">
                <!--
                  Status is output, the button is the control: the same shared badge
                  the teacher's roster uses, so one status reads identically in both.
                -->
                <SchoolEnrollmentStatusBadge :status="studentClass.status" />

                <q-btn
                  dense
                  rounded
                  unelevated
                  no-caps
                  color="info"
                  size="sm"
                  class="student-class-details"
                  :icon="mdiInformationOutline"
                  :label="$t('DETAILS')"
                  :aria-label="`${$t('DETAILS')} — ${$t(metaFor(studentClass).labelKey)}`"
                  @click="openStatus(studentClass)"
                />
              </q-card-section>
            </q-card>
          </div>
        </div>
      </div>
    </template>

    <SchoolEnrollmentStatusDialog
      v-model="statusDialogOpen"
      :status="activeClass?.status"
      :class-name="activeClass?.className"
      :school-name="activeClass?.schoolName"
    />
  </section>
</template>

<style scoped lang="scss">
.student-classes-heading {
  color: var(--text-primary);
}

.student-classes-empty {
  padding: 16px;
  border-radius: 12px;
  border: 1px dashed var(--border-subtle);
  color: var(--text-muted);
}

/*
 * Themed from the shared surface tokens so the cards follow Light/Dark/System.
 * The status color rides in as a CSS var set on the element, which keeps the
 * left accent bar in sync with the chip without a class per status.
 */
.student-class-card {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  background-color: var(--surface-card);
  color: var(--text-primary);
  border: 1px solid var(--border-subtle);
  border-left: 4px solid var(--status-color, var(--border-subtle));
  border-radius: 12px;
  transition: border-color 0.2s ease;
}

.student-class-name {
  color: var(--text-primary);
}

.student-class-school {
  color: var(--text-muted);
}

/* Keeps the filled pill from crowding its label. */
.student-class-details {
  padding-inline: 10px;
}

.student-semester-tag {
  font-size: 11px;
}
</style>
